/**
 * Goals API Client
 * BP-06-03: Care Team & Goals Frontend
 */

import apiClient from '@/core/lib/api-client';
import type {
  Goal,
  GoalProgressEntry,
  GoalSummary,
  IEPGoalReport,
  GoalsListResponse,
  GoalProgressResponse,
  CreateGoalRequest,
  UpdateGoalRequest,
  CreateProgressEntryRequest,
  UpdateProgressEntryRequest,
  GoalFilters,
  GoalSortOptions,
  GoalsExportOptions,
} from '@/features/goals/types/goalsTypes';

// Note: apiClient already has /api/v1 as base URL
const BASE_URL = '';

/**
 * Goals API
 */
export const goalsApi = {
  /**
   * Get all goals for a child
   */
  getAll: async (
    childId: string,
    filters?: GoalFilters,
    sort?: GoalSortOptions,
    page?: number,
    limit?: number
  ): Promise<GoalsListResponse> => {
    const params = new URLSearchParams();
    
    if (filters?.goalType) params.append('goal_type', filters.goalType);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.category) params.append('category', filters.category);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.isIepGoal !== undefined) params.append('is_iep_goal', String(filters.isIepGoal));
    if (filters?.careTeamMemberId) params.append('care_team_member_id', filters.careTeamMemberId);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.startDateFrom) params.append('start_date_from', filters.startDateFrom);
    if (filters?.startDateTo) params.append('start_date_to', filters.startDateTo);
    if (filters?.targetDateFrom) params.append('target_date_from', filters.targetDateFrom);
    if (filters?.targetDateTo) params.append('target_date_to', filters.targetDateTo);
    
    if (sort) {
      params.append('sort_by', sort.field);
      params.append('sort_direction', sort.direction);
    }
    
    if (page) params.append('page', String(page));
    if (limit) params.append('limit', String(limit));

    const queryString = params.toString();
    const url = `${BASE_URL}/children/${childId}/goals${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiClient.get<GoalsListResponse>(url);
    return response.data;
  },

  /**
   * Get a single goal by ID
   */
  getById: async (goalId: string): Promise<Goal> => {
    const response = await apiClient.get<Goal>(
      `${BASE_URL}/goals/${goalId}`
    );
    return response.data;
  },

  /**
   * Create a new goal
   */
  create: async (
    childId: string,
    data: CreateGoalRequest
  ): Promise<Goal> => {
    const response = await apiClient.post<Goal>(
      `${BASE_URL}/children/${childId}/goals`,
      data
    );
    return response.data;
  },

  /**
   * Update a goal
   */
  update: async (
    goalId: string,
    data: UpdateGoalRequest
  ): Promise<Goal> => {
    const response = await apiClient.patch<Goal>(
      `${BASE_URL}/goals/${goalId}`,
      data
    );
    return response.data;
  },

  /**
   * Delete a goal
   */
  delete: async (goalId: string): Promise<void> => {
    await apiClient.delete(`${BASE_URL}/goals/${goalId}`);
  },

  /**
   * Mark a goal as achieved
   */
  markAchieved: async (goalId: string): Promise<Goal> => {
    const response = await apiClient.post<Goal>(
      `${BASE_URL}/goals/${goalId}/achieve`
    );
    return response.data;
  },

  /**
   * Discontinue a goal
   */
  discontinue: async (goalId: string, reason?: string): Promise<Goal> => {
    const response = await apiClient.post<Goal>(
      `${BASE_URL}/goals/${goalId}/discontinue`,
      { reason }
    );
    return response.data;
  },

  /**
   * Get progress entries for a goal
   */
  getProgress: async (
    goalId: string,
    page?: number,
    limit?: number
  ): Promise<GoalProgressResponse> => {
    const params = new URLSearchParams();
    if (page) params.append('page', String(page));
    if (limit) params.append('limit', String(limit));

    const queryString = params.toString();
    const url = `${BASE_URL}/goals/${goalId}/progress${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiClient.get<GoalProgressResponse>(url);
    return response.data;
  },

  /**
   * Add a progress entry
   */
  addProgress: async (
    goalId: string,
    data: CreateProgressEntryRequest
  ): Promise<GoalProgressEntry> => {
    const formData = new FormData();
    formData.append('entry_date', data.entryDate);
    formData.append('value', data.value);
    formData.append('percentage', String(data.percentage));
    if (data.notes) formData.append('notes', data.notes);
    if (data.sessionType) formData.append('session_type', data.sessionType);
    
    if (data.attachments) {
      data.attachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });
    }

    const response = await apiClient.post<GoalProgressEntry>(
      `${BASE_URL}/goals/${goalId}/progress`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Update a progress entry
   */
  updateProgress: async (
    entryId: string,
    data: UpdateProgressEntryRequest
  ): Promise<GoalProgressEntry> => {
    const response = await apiClient.patch<GoalProgressEntry>(
      `${BASE_URL}/progress/${entryId}`,
      data
    );
    return response.data;
  },

  /**
   * Delete a progress entry
   */
  deleteProgress: async (entryId: string): Promise<void> => {
    await apiClient.delete(`${BASE_URL}/progress/${entryId}`);
  },

  /**
   * Get goals summary for a child
   */
  getSummary: async (childId: string): Promise<GoalSummary> => {
    const response = await apiClient.get<GoalSummary>(
      `${BASE_URL}/children/${childId}/goals/summary`
    );
    return response.data;
  },

  /**
   * Get IEP goals report
   */
  getIEPReport: async (childId: string): Promise<IEPGoalReport> => {
    const response = await apiClient.get<IEPGoalReport>(
      `${BASE_URL}/children/${childId}/goals/iep-report`
    );
    return response.data;
  },

  /**
   * Export goals to CSV or PDF
   */
  export: async (
    childId: string,
    options: GoalsExportOptions
  ): Promise<Blob> => {
    const params = new URLSearchParams();
    params.append('format', options.format);
    if (options.goalType) params.append('goal_type', options.goalType);
    if (options.status) params.append('status', options.status);
    if (options.includeProgress) params.append('include_progress', 'true');

    const response = await apiClient.get(
      `${BASE_URL}/children/${childId}/goals/export?${params.toString()}`,
      { responseType: 'blob' }
    );
    return response.data;
  },
};

/**
 * Helper function to calculate progress percentage from entries
 */
export function calculateProgressPercentage(
  entries: GoalProgressEntry[],
  targetValue: string | null,
  measurementType: string
): number {
  if (entries.length === 0) return 0;
  
  const latestEntry = entries[0];
  
  if (measurementType === 'percentage') {
    return latestEntry.percentage;
  }
  
  if (measurementType === 'yes_no') {
    return latestEntry.value.toLowerCase() === 'yes' ? 100 : 0;
  }
  
  if (targetValue && measurementType === 'count') {
    const current = parseFloat(latestEntry.value) || 0;
    const target = parseFloat(targetValue) || 1;
    return Math.min(100, Math.round((current / target) * 100));
  }
  
  return latestEntry.percentage;
}

/**
 * Helper function to format progress value for display
 */
export function formatProgressValue(
  value: string,
  measurementType: string
): string {
  switch (measurementType) {
    case 'percentage':
      return `${value}%`;
    case 'count':
      return value;
    case 'rating':
      return `${value}/5`;
    case 'yes_no':
      return value.toLowerCase() === 'yes' ? 'Yes' : 'No';
    default:
      return value;
  }
}

/**
 * Helper function to get status badge color class
 */
export function getStatusColorClass(status: string): string {
  const colors: Record<string, string> = {
    not_started: 'bg-gray-100 text-gray-700',
    in_progress: 'bg-sage-100 text-sage-700',
    achieved: 'bg-green-100 text-green-700',
    discontinued: 'bg-terracotta-100 text-terracotta-700',
  };
  return colors[status] || 'bg-gray-100 text-gray-700';
}

/**
 * Helper function to get priority badge color class
 */
export function getPriorityColorClass(priority: string): string {
  const colors: Record<string, string> = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-blue-100 text-blue-700',
  };
  return colors[priority] || 'bg-gray-100 text-gray-700';
}

